"use client";

import { ShieldCheck, Lock, Activity, Quote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurDefensivePhilosophy_352() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const principles = [{
    number: "01",
    label: "SWISS PRECISION",
    description: "Direct sourcing from certified manufacturers ensuring top-tier quality standards.",
    icon: ShieldCheck
  }, {
    number: "02",
    label: "INDUSTRIAL SCALE",
    description: "Comprehensive distribution network serving B2B and B2C clients across Europe.",
    icon: Lock
  }, {
    number: "03",
    label: "TECHNICAL EXCELLENCE",
    description: "Expert support for complex construction projects with competitive pricing models.",
    icon: Activity
  }];
  const dataTags = [{
    label: "ISO CERTIFIED",
    top: "12%",
    left: "18%"
  }, {
    label: "EUROPEAN LOGISTICS",
    top: "28%",
    left: "62%"
  }, {
    label: "RAL COLOR MATCH",
    top: "58%",
    left: "10%"
  }, {
    label: "STEEL SOLUTIONS",
    top: "72%",
    left: "58%"
  }];
  return <section data-section-id="390" id="our-industrial-legacy" className="relative bg-background text-foreground overflow-hidden pt-32 md:pt-40 pb-24 md:pb-30">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-1/4 h-[600px] w-[600px] rounded-lg blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)",
      opacity: 0.18
    }} />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-8 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span className="hidden md:inline">BUJANOVAC / SERBIA / EUROPE</span>
          <span>v2.4.1</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-12 lg:gap-10 items-start">
          <div ref={leftRef} data-index={0} className={`transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="inline-flex items-center gap-2 font-mono text-xs tracking-widest text-[color:var(--primary)] mb-8">
              <span className="inline-block h-px w-8 bg-[color:var(--primary)]" />
              OUR INDUSTRIAL LEGACY
            </div>

            <h1 className="font-default font-semibold leading-[1.02] tracking-tight text-[44px] sm:text-[60px] lg:text-[80px]">
              <span className="block">
                <span className="text-[color:var(--primary)]">Engineered</span> quality
                <span className="text-[color:var(--primary)]">.</span>
              </span>
              <span className="block">
                <span className="text-[color:var(--primary)]">Built</span> for durability
                <span className="text-[color:var(--primary)]">.</span>
              </span>
              <span className="block">
                <span className="text-[color:var(--primary)]">Delivered</span> with precision
                <span className="text-[color:var(--primary)]">.</span>
              </span>
            </h1>

            <p className="mt-8 max-w-[560px] text-lg md:text-[19px] leading-relaxed text-muted-foreground">
              Swiss Solutions d.o.o. brings premium construction materials to the Balkan market. 
              From high-performance sandwich panels to complete steel building packages, 
              we ensure every project meets the highest European standards of structural integrity.
            </p>

            <div className="mt-12 flex flex-col gap-4">
              {principles.map((p, i) => {
              const Icon = p.icon;
              return <div key={p.number} data-index={i} className="group flex items-start gap-5 rounded-lg border border-border bg-card/40 backdrop-blur-sm px-5 py-4 transition-all duration-300 hover:border-[color:var(--primary)]/50 hover:bg-card/70">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/5">
                      <Icon className="h-5 w-5 text-[color:var(--primary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-mono text-xs tracking-widest text-[color:var(--primary)]">
                          PRINCIPLE {p.number}
                        </span>
                        <span className="font-mono text-xs tracking-widest text-muted-foreground">
                          —
                        </span>
                        <span className="font-default text-base font-semibold text-foreground">
                          {p.label}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                        {p.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={rightRef} data-index={1} className={`relative transition-all duration-1000 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative aspect-square w-full max-w-[560px] mx-auto">
              <div aria-hidden="true" className="absolute inset-0 rounded-lg blur-3xl" style={{
              background: "radial-gradient(circle at 50% 50%, var(--primary) 0%, transparent 60%)",
              opacity: 0.25
            }} />

              <svg viewBox="0 0 400 400" className="relative h-full w-full" style={{
              animation: "spin 60s linear infinite"
            }}>
                <defs>
                  <linearGradient id="latticeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.2" />
                  </linearGradient>
                  <radialGradient id="latticeFill" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                  </radialGradient>
                </defs>

                <polygon points="200,30 340,110 340,290 200,370 60,290 60,110" fill="url(#latticeFill)" stroke="url(#latticeGrad)" strokeWidth="1.5" />
                <polygon points="200,80 295,135 295,265 200,320 105,265 105,135" fill="none" stroke="var(--primary)" strokeOpacity="0.5" strokeWidth="1" />
                <polygon points="200,130 250,160 250,240 200,270 150,240 150,160" fill="none" stroke="var(--primary)" strokeOpacity="0.7" strokeWidth="1" />

                {[[200, 30, 200, 130], [340, 110, 250, 160], [340, 290, 250, 240], [200, 370, 200, 270], [60, 290, 150, 240], [60, 110, 150, 160], [200, 30, 340, 290], [200, 30, 60, 290], [340, 110, 60, 290], [60, 110, 340, 290], [60, 110, 200, 370], [340, 110, 200, 370]].map(([x1, y1, x2, y2], i) => <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--primary)" strokeOpacity="0.25" strokeWidth="0.75" />)}

                {[[200, 30], [340, 110], [340, 290], [200, 370], [60, 290], [60, 110], [200, 130], [250, 160], [250, 240], [200, 270], [150, 240], [150, 160], [200, 200]].map(([cx, cy], i) => <g key={i}>
                    <circle cx={cx} cy={cy} r="4" fill="var(--primary)" />
                    <circle cx={cx} cy={cy} r="8" fill="var(--primary)" fillOpacity="0.2" />
                  </g>)}

                <line x1="0" y1="200" x2="400" y2="200" stroke="var(--primary)" strokeOpacity="0.4" strokeWidth="0.5" strokeDasharray="2,4" />
              </svg>

              {dataTags.map((tag, i) => <div key={tag.label} data-index={i} className="absolute" style={{
              top: tag.top,
              left: tag.left
            }}>
                  <div className="relative">
                    <Badge variant="outline" className="font-mono text-[10px] tracking-widest bg-card/80 backdrop-blur-md border-[color:var(--primary)]/40 text-[color:var(--primary)] px-3 py-1.5 shadow-md">
                      <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-md bg-[color:var(--primary)] animate-pulse" />
                      {tag.label}
                    </Badge>
                  </div>
                </div>)}

              <span className="absolute top-0 left-0 h-5 w-5 border-t border-l border-[color:var(--primary)]/60" />
              <span className="absolute top-0 right-0 h-5 w-5 border-t border-r border-[color:var(--primary)]/60" />
              <span className="absolute bottom-0 left-0 h-5 w-5 border-b border-l border-[color:var(--primary)]/60" />
              <span className="absolute bottom-0 right-0 h-5 w-5 border-b border-r border-[color:var(--primary)]/60" />
            </div>

            <div className="mt-6 mx-auto max-w-[560px] flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-t border-border pt-3">
              <span>DISTRIBUTION.NETWORK/ACTIVE</span>
              <span className="text-[color:var(--primary)]">● SUPPLY CHAIN</span>
              <span>QUALITY 100%</span>
            </div>
          </div>
        </div>

        <div ref={quoteRef} data-index={2} className={`relative mt-24 md:mt-32 transition-all duration-700 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="relative mx-auto max-w-5xl border-t border-b border-border py-10 md:py-14">
            <span className="absolute top-0 left-0 h-4 w-4 border-t-2 border-l-2 border-[color:var(--primary)]" />
            <span className="absolute top-0 right-0 h-4 w-4 border-t-2 border-r-2 border-[color:var(--primary)]" />
            <span className="absolute bottom-0 left-0 h-4 w-4 border-b-2 border-l-2 border-[color:var(--primary)]" />
            <span className="absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-[color:var(--primary)]" />

            <Quote className="absolute left-6 top-6 h-5 w-5 text-[color:var(--primary)]/40" />

            <blockquote className="px-8 md:px-12 text-center">
              <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-foreground">
                "Building the future requires more than just materials; it requires 
                the precision of Swiss standards and the reliability of a trusted partner."
              </p>
              <footer className="mt-6 font-mono text-xs tracking-widest text-muted-foreground">
                — SWISS SOLUTIONS D.O.O. · PREMIUM CONSTRUCTION PARTNER
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
    </section>;
}